import { Stack } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiRepeat } from "react-icons/fi";
import { SourcingEventStatus, SourcingEventV2Dto } from "../../../autogen/bff-api";
import { MinimumRequirementsSection } from "../../sourcing-events/portal/view-single/MinimumRequirementsSection";
import { SectionCard } from "../../sourcing-events/portal/view-single/SectionCard";
import { AboutSection } from "./AboutSection";
import { ClassificationSection } from "./ClassificationSection";
import { ContractSection } from "./ContractSection";
import { CriteriaSection } from "./CriteriaSection";
import { DocumentsSection } from "./DocumentsSection";
import { InvitesSection } from "./InvitesSection";
import { PublicationsSection } from "./PublicationsSection";
import { ViewingsSection } from "./ViewingsSection";
import { ChangeNoticeSection } from "./changes/ChangeNoticeSection";
import { RoundSummary } from "./negotiation/RoundSummary";
import { RoundDocumentsSection } from "./rounds/RoundDocumentsSection";
import { useRoundSelection } from "./rounds/RoundSelectionContext";

interface Props {
  event: SourcingEventV2Dto;
}

export const RequestTab = ({ event }: Props) => {
  const { t } = useTranslation();
  const { selectedRound } = useRoundSelection();
  const { eventData } = event;
  const publicContext = event.eventContext.type === "Public" ? event.eventContext : null;
  const privateContext = event.eventContext.type === "Private" ? event.eventContext : null;

  // ADR-033 D2: with a round selected, the request tab IS that round's request.
  if (selectedRound) {
    const lifecycleLocked =
      event.status !== SourcingEventStatus.Published && event.status !== SourcingEventStatus.Evaluation;
    return (
      <Stack spacing="5">
        <SectionCard icon={FiRepeat} title={`${t("Negotiation round")} ${selectedRound.roundNumber}`}>
          <RoundSummary round={selectedRound} lifecycleLocked={lifecycleLocked} />
        </SectionCard>
        <RoundDocumentsSection event={event} round={selectedRound} />
      </Stack>
    );
  }

  return (
    <Stack spacing="5">
      <ChangeNoticeSection event={event} />
      <AboutSection
        eventData={eventData}
        derivedPlaceOfPerformance={event.derivedPlaceOfPerformance}
        isPrivateEvent={!!privateContext}
      />
      {publicContext && <ClassificationSection publicContext={publicContext} />}
      {/* Award criteria and minimum requirements are optional outside the public flow, so both
          follow the data rather than the regime — a private procurement that has them shows them. */}
      {(publicContext || eventData.awardCriteria) && (
        <CriteriaSection eventData={eventData} showSelectionCriteria={!!publicContext} />
      )}
      <MinimumRequirementsSection requirements={eventData.minimumRequirements ?? []} />
      {publicContext && <ContractSection eventData={eventData} publicContext={publicContext} />}
      <DocumentsSection eventId={event.id} documents={eventData.documents ?? []} />
      {publicContext && <PublicationsSection publications={publicContext.publications ?? []} />}
      {privateContext && <InvitesSection event={{ ...event, eventContext: privateContext }} />}
      {event.status !== SourcingEventStatus.Draft && <ViewingsSection viewings={event.viewings ?? []} />}
    </Stack>
  );
};
